import React, { useEffect } from 'react';

import type { IProps } from './oauth-button-container';
import { ActionForAuthentication } from '../../types';
import { OAuthMethod } from './types';

import toaster, { Theme } from '../../../../shared/toaster';
import Button from '../../../../shared/design-system/ui/button';

import { Google } from '../../../../shared/svg/google';
import { Microsoft } from '../../../../shared/svg/microsoft';

import {
  executeOnRequestStatus,
  executeOnErrorWithErrorCheck,
  getCurrentTimeZone,
  redirectWithRefresh,
} from '../../../../shared/utils';
import { mergeMarketingAttributionFromLocation } from '../../../../shared/utils/gtm-attribution';
import useGoogleOneTap from './use-google-one-tap';

const OAuthButtons: React.FC<IProps> = ({
  type = ActionForAuthentication.Login,
  className = '',
  getOAuthRedirectUrlRequestStatus,
  getOAuthRedirectUrlRequestError,
  authUrl,
  email,
  userId,
  getOAuthRedirectUrl,
  resetGetOAuthRedirectUrlRequest,
}) => {
  const [loadingMethod, setLoadingMethod] = React.useState<OAuthMethod | null>(
    null,
  );

  useEffect(() => {
    executeOnRequestStatus({
      status: getOAuthRedirectUrlRequestStatus,
      onSuccess: () => {
        redirectWithRefresh(authUrl);
        resetGetOAuthRedirectUrlRequest();
        setLoadingMethod(null);
      },
      onFailed: () => {
        resetGetOAuthRedirectUrlRequest();
        setLoadingMethod(null);
        executeOnErrorWithErrorCheck({
          error: getOAuthRedirectUrlRequestError,
          onError: () => {
            toaster.error(getOAuthRedirectUrlRequestError, {
              theme: Theme.New,
            });
          },
        });
      },
    });
  }, [getOAuthRedirectUrlRequestStatus]);

  // Display Google One Tap on Login/Signup pages.
  useGoogleOneTap({
    type,
  });

  const handleOAuthClick = (method: OAuthMethod) => {
    mergeMarketingAttributionFromLocation();
    const timeZone = getCurrentTimeZone();
    const referralId = window.tolt_referral ?? null;
    setLoadingMethod(method);
    getOAuthRedirectUrl(method, {
      timeZone,
      invitedEmail: email,
      userId,
      action: type,
      landingPage: window.location.href,
      ...(type === ActionForAuthentication.Signup &&
        referralId && { rewardfulReferralId: referralId }),
    });
  };

  const getBtnLabel = (method: OAuthMethod) => {
    const label =
      type === ActionForAuthentication.Signup
        ? 'Sign up with '
        : 'Log in with ';
    if (method === OAuthMethod.Google) return `${label} Google`;
    if (method === OAuthMethod.Microsoft) return `${label} Microsoft`;
    return '';
  };

  return (
    <div className={`oauth-buttons-container ${className}`}>
      <div className="oauth-buttons">
        <Button
          type="button"
          variant="secondary"
          className="oauth-button google-button"
          onClick={() => handleOAuthClick(OAuthMethod.Google)}
          isLoading={loadingMethod === OAuthMethod.Google}
          loadingText={getBtnLabel(OAuthMethod.Google)}
          disabled={!!loadingMethod}
        >
          <Google />
          <span>{getBtnLabel(OAuthMethod.Google)}</span>
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="oauth-button microsoft-button"
          onClick={() => handleOAuthClick(OAuthMethod.Microsoft)}
          isLoading={loadingMethod === OAuthMethod.Microsoft}
          loadingText={getBtnLabel(OAuthMethod.Microsoft)}
          disabled={!!loadingMethod}
        >
          <Microsoft />
          <span>{getBtnLabel(OAuthMethod.Microsoft)}</span>
        </Button>
      </div>
      {(type === ActionForAuthentication.Login ||
        type === ActionForAuthentication.Signup) && (
        <div className="oauth-divider">
          <span>OR</span>
        </div>
      )}
    </div>
  );
};

export default OAuthButtons;
